import { useEffect, useRef } from 'react'
import {
  AuthApiError,
  fetchCurrentSession,
  fetchMembershipSummary,
} from '@/features/auth/services/auth-service'
import { createLogger } from '@/shared/logging/logger'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'

const logger = createLogger('AuthBootstrap')

export function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const initialized = useAuthSessionStore((state) => state.initialized)
  const setSession = useAuthSessionStore((state) => state.setSession)
  const clearSession = useAuthSessionStore((state) => state.clearSession)
  const setInitialized = useAuthSessionStore((state) => state.setInitialized)
  const updateMembership = useAuthSessionStore((state) => state.updateMembership)
  const bootstrapStartedRef = useRef(false)

  useEffect(() => {
    if (bootstrapStartedRef.current) {
      return
    }

    bootstrapStartedRef.current = true

    void (async () => {
      const fallbackSession = useAuthSessionStore.getState().session

      try {
        const session = await fetchCurrentSession()

        if (session) {
          setSession(session)

          try {
            const membership = await fetchMembershipSummary()
            if (membership) {
              updateMembership(membership)
            }
          } catch (error) {
            logger.warn('Failed to refresh membership summary during bootstrap', error)
          }
        } else {
          clearSession()
        }
      } catch (error) {
        logger.warn('Failed to refresh auth session during bootstrap', error)
        const isUnauthorized =
          error instanceof AuthApiError && (error.status === 401 || error.status === 403)

        if (isUnauthorized || !fallbackSession?.user?.id) {
          clearSession()
        }
      } finally {
        setInitialized(true)
      }
    })()
  }, [clearSession, setInitialized, setSession, updateMembership])

  if (!initialized) {
    return <div className="min-h-screen bg-background" aria-hidden="true" />
  }

  return <>{children}</>
}